/**
 * TimeTrackingErrorHandler - Domain-specific error handling for time tracking operations
 */

import { HttpError } from '../../../core/services/HttpService';
import dialogUtil from '../../../shared/utils/dialog';

export class TimeTrackingErrorHandler {
  private static instance: TimeTrackingErrorHandler;

  static getInstance(): TimeTrackingErrorHandler {
    if (!TimeTrackingErrorHandler.instance) {
      TimeTrackingErrorHandler.instance = new TimeTrackingErrorHandler();
    }
    return TimeTrackingErrorHandler.instance;
  }

  /**
   * Handle time tracking specific HTTP errors
   * Returns true if error was handled, false if global handler should handle it
   */
  handle(error: HttpError): boolean {
    switch (error.status) {
      case 400:
        return this.handleBadRequest(error);

      case 409:
        return this.handleConflict(error);

      case 422:
        return this.handleValidationError(error);

      default:
        // Let global handler deal with other errors (401, 403, 404, 5xx, etc.)
        return false;
    }
  }

  private handleBadRequest(error: HttpError): boolean {
    console.log('Time tracking bad request:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    // Handle specific time tracking validation errors
    if (error.data?.start_time || error.data?.end_time) {
      dialogUtil.showToast({
        title: 'Invalid Time Format',
        message: 'Please check start and end times are properly formatted',
        type: 'error',
      });
      return true;
    }

    if (error.data?.break_start || error.data?.break_end) {
      dialogUtil.showToast({
        title: 'Invalid Break Time',
        message: 'Break times must be within the work period',
        type: 'error',
      });
      return true;
    }

    if (error.data?.employee) {
      dialogUtil.showToast({
        title: 'Employee Required',
        message: 'Please select a valid employee',
        type: 'error',
      });
      return true;
    }

    // Generic bad request
    dialogUtil.showToast({
      title: 'Invalid Data',
      message: errorMessage,
      type: 'error',
    });
    return true;
  }

  private handleConflict(error: HttpError): boolean {
    console.log('Time tracking conflict:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    // Handle overlapping time tracking entries
    if (errorMessage.toLowerCase().includes('overlap') || 
        errorMessage.toLowerCase().includes('conflict')) {
      dialogUtil.showModal({
        title: 'Time Tracking Conflict',
        content: 'This time tracking entry overlaps with an existing entry. Please adjust the times or delete the conflicting entry.',
        primaryAction: {
          label: 'OK',
          onPress: () => {}
        },
        variant: 'warning',
        closeOnBackdropPress: true,
      });
      return true;
    }

    // Generic conflict
    dialogUtil.showToast({
      title: 'Conflict',
      message: errorMessage,
      type: 'error',
    });
    return true;
  }

  private handleValidationError(error: HttpError): boolean {
    console.log('Time tracking validation error:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    // Handle field validation errors
    if (error.data && typeof error.data === 'object') {
      const fieldErrors = [];
      
      if (error.data.start_time) fieldErrors.push(`Start time: ${error.data.start_time}`);
      if (error.data.end_time) fieldErrors.push(`End time: ${error.data.end_time}`);
      if (error.data.break_start) fieldErrors.push(`Break start: ${error.data.break_start}`);
      if (error.data.break_end) fieldErrors.push(`Break end: ${error.data.break_end}`);
      if (error.data.employee) fieldErrors.push(`Employee: ${error.data.employee}`);
      if (error.data.work_area) fieldErrors.push(`Work area: ${error.data.work_area}`);
      
      if (fieldErrors.length > 0) {
        dialogUtil.showModal({
          title: 'Validation Errors',
          content: fieldErrors.join('\n'),
          primaryAction: {
            label: 'OK',
            onPress: () => {}
          },
          variant: 'warning',
          closeOnBackdropPress: true,
        });
        return true;
      }
    }

    // Generic validation error
    dialogUtil.showToast({
      title: 'Validation Error',
      message: errorMessage,
      type: 'error',
    });
    return true;
  }

  /**
   * Extract meaningful error message from error response
   */
  private extractErrorMessage(error: HttpError): string {
    if (error.data?.detail) {
      return error.data.detail;
    }
    
    if (error.data?.message) {
      return error.data.message;
    }
    
    if (error.data && typeof error.data === 'object') {
      // Try to extract field-specific errors
      const fieldErrors = Object.entries(error.data)
        .filter(([key, value]) => typeof value === 'string' || Array.isArray(value))
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join('; ');
      
      if (fieldErrors) {
        return fieldErrors;
      }
      
      return JSON.stringify(error.data);
    }
    
    return 'No additional information available';
  }
}

// Export singleton instance
const timeTrackingErrorHandler = TimeTrackingErrorHandler.getInstance();
export default timeTrackingErrorHandler; 